import type {
  SoundCodeResponse,
  QrCodeResponse,
  VenueAttendanceResponse,
  ManualCheckInResponse,
  AccommodationFilter,
  AccommodationImportItem,
  AccommodationImportResponse,
  AccommodationParticipantsResponse,
  AccommodationResponse,
  AccommodationStatus,
  CertStatus,
  CheckInResponse,
  EventSettings,
  GetSlotResponse,
  LoginResponse,
  OtpRequestResponse,
  ParticipantProfile,
  PassIssued,
  PassVerdict,
  PassCheckinVerdict,
  CertificateLayout,
  CertificateState,
  CertificateGenerateResponse,
  IssuedCertificate,
  CertificatePerson,
  Idea,
  IdeaCategory,
  IdeaModule,
  IdeaStatus,
  Announcement,
  AnnouncementLevel,
  QrGetSlotResponse,
  SaveProfileResponse,
  ExtendSlotResponse,
  AttendanceSummaryResponse,
  AttendanceDetailResponse,
  AdminUsersResponse,
  AdminPasswordResponse,
  AdminCreateUserResponse,
  AdminEventsResponse,
} from './types'
import * as mock from '../mock/fixtures'

/**
 * In dev the API is bare (`http://localhost:8000/getSlot`); in production it sits
 * behind the reverse proxy that prefixes /api (`https://medical.uitm.edu.my/api/getSlot`).
 * Both are just a base string — set VITE_API_BASE accordingly.
 */
export const API_BASE: string =
  import.meta.env.VITE_API_BASE ?? 'http://localhost:8000'

/** `id_event` threads through nearly every request body. Omit it and you get another event's data. */
/*
 * The running event's id, set once at boot from `event.is_current`.
 *
 * `let`, not `const`, and deliberately: ESM live bindings mean the twenty-odd request
 * bodies below - all of which read it inside a function, at call time - pick up the
 * resolved value without a single call site changing.
 *
 * It starts at 0, which is not a valid id. There is no VITE_ID_EVENT to fall back on
 * any more: an event this app cannot ask the server about is one it should not be
 * guessing at, so main.tsx refuses to render rather than sending 0 anywhere. Seeing 0
 * in a request body means something rendered before boot finished.
 */
export let ID_EVENT: number = 0

/** Called once from main.tsx, after the event has been resolved. */
export function setIdEvent(next: number): void {
  if (Number.isInteger(next) && next > 0) ID_EVENT = next
}

const PROBE_TIMEOUT_MS = 2500
const CALL_TIMEOUT_MS = 12000

/*
 * 'mock' is only ever chosen on purpose (VITE_FORCE_MOCK=1, the demo build). A probe
 * that fails yields 'down': main.tsx then renders the cannot-reach screen instead of
 * the app, so a real delegate never sees the fixtures' fictional programme under a
 * banner they may not read. Fixtures are a demo, not a fallback.
 */
export type ApiMode = 'probing' | 'live' | 'mock' | 'down'

let mode: ApiMode = 'probing'
const listeners = new Set<(m: ApiMode) => void>()

export function getMode(): ApiMode {
  return mode
}

export function subscribeMode(fn: (m: ApiMode) => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function setMode(next: ApiMode) {
  if (next === mode) return
  mode = next
  listeners.forEach((fn) => fn(mode))
}

/** Forced by VITE_FORCE_MOCK=1, useful for design work with the API up but the DB down. */
const FORCED_MOCK = import.meta.env.VITE_FORCE_MOCK === '1'

async function withTimeout<T>(p: (signal: AbortSignal) => Promise<T>, ms: number): Promise<T> {
  const ac = new AbortController()
  const timer = setTimeout(() => ac.abort(), ms)
  try {
    return await p(ac.signal)
  } finally {
    clearTimeout(timer)
  }
}

/**
 * One-shot health probe against the API's own /test route. Resolved once and
 * cached — every later call reuses the verdict unless a live call fails outright.
 */
let probe: Promise<ApiMode> | null = null

export function probeApi(): Promise<ApiMode> {
  if (FORCED_MOCK) {
    setMode('mock')
    return Promise.resolve<ApiMode>('mock')
  }
  probe ??= withTimeout(
    (signal) => fetch(`${API_BASE}/test`, { signal }),
    PROBE_TIMEOUT_MS,
  )
    .then((r) => (r.ok ? 'live' : 'down') as ApiMode)
    .catch(() => 'down' as ApiMode)
    .then((m) => {
      setMode(m)
      return m
    })
  return probe
}

/** Thrown for a reachable API that answered with a non-2xx. Not a fallback trigger. */
/** Fired on window when a token-bearing call is refused with 401. SessionProvider listens. */
export const SESSION_LOST_EVENT = 'cpd:session-lost'

export class ApiError extends Error {
  /** The server's JSON body when it sent one, for handlers that need the reason. */
  readonly body: Record<string, unknown> | null
  constructor(readonly status: number, message: string, body: Record<string, unknown> | null = null) {
    super(message)
    this.name = 'ApiError'
    this.body = body
  }
}

async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`

  const res = await withTimeout(
    (signal) =>
      fetch(`${API_BASE}${path}`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        credentials: 'include',
        signal,
      }),
    CALL_TIMEOUT_MS,
  )

  if (!res.ok) {
    let body: Record<string, unknown> | null = null
    try {
      const text = await res.text()
      body = text ? (JSON.parse(text) as Record<string, unknown>) : null
    } catch {
      body = null
    }
    /*
     * A 401 on a call that carried a token means the session is gone on the server -
     * Redis restarted, the token was swept, or someone signed this device out. Nothing
     * the caller can do fixes that, so say so once here rather than leaving every
     * screen to report "Could not load the programme" against a dead session.
     */
    if (res.status === 401 && token) {
      window.dispatchEvent(new CustomEvent(SESSION_LOST_EVENT))
    }
    throw new ApiError(res.status, `${path} responded ${res.status}`, body)
  }
  // Some handlers (/setting_apps/update) end without sending a body at all.
  const text = await res.text()
  return (text ? JSON.parse(text) : {}) as T
}

/**
 * Run the live call, or the fixture if the boot probe found no API at all.
 *
 * The decision is made once, by the probe, and never revisited. A request that fails
 * *after* we know the API is up is surfaced as an error — it is never quietly swapped
 * for a fixture.
 *
 * That rule is load-bearing rather than tidy. `/altLogin` never replies when the email
 * is unknown (its `if (user !== undefined)` has no `else`), so the request hangs until
 * this client aborts it. Under a demote-on-failure policy that abort would fall
 * through to `mock.passwordLogin`, which accepts any non-empty password — so a live
 * API plus one mistyped address would sign the user in as a fictional participant
 * carrying invented attendance. Fixtures are for a backend that is absent, not for one
 * that is misbehaving.
 */
async function call<T>(live: () => Promise<T>, fixture: () => T | Promise<T>): Promise<T> {
  if (mode === 'probing') await probeApi()
  if (mode === 'mock') return fixture()
  return live()
}

/* ------------------------------------------------------------------ auth */

export const api = {
  loginWithGoogle: (googleToken: string) =>
    call<LoginResponse>(
      () => post('/validateGoogle', { googleToken, id_event: ID_EVENT }),
      () => mock.loginResponse(),
    ),

  loginWithPassword: (email: string, password: string) =>
    call<LoginResponse>(
      () => post('/altLogin', { email, password, id_event: ID_EVENT }),
      () => mock.passwordLogin(email, password),
    ),

  requestOtp: (email: string) =>
    call<OtpRequestResponse>(
      () => post('/otp', { email }),
      () => mock.requestOtp(email),
    ),

  verifyOtp: (email: string, otp: string) =>
    call<LoginResponse>(
      () => post('/checkOTP', { otp, email, id_event: ID_EVENT }),
      () => mock.verifyOtp(email, otp),
    ),

  /** Sets `verified_name` so the app stops asking. */
  saveName: (email: string, updatedName: string, token: string) =>
    call<{ is_success: boolean }>(
      () => post('/saveName', { email, updatedName, id_event: ID_EVENT }, token),
      () => ({ is_success: true }),
    ),

  getParticipantProfile: (email: string, token: string) =>
    call<ParticipantProfile>(
      () => post('/participant/profile', { email, id_event: ID_EVENT }, token),
      () => mock.getParticipantProfile(),
    ),

  saveParticipantProfile: (
    email: string,
    token: string,
    fullName: string,
    trackId: number,
  ) =>
    call<SaveProfileResponse>(
      () => post(
        '/participant/profile/save',
        { email, id_event: ID_EVENT, full_name: fullName, track_id: trackId },
        token,
      ),
      () => mock.saveParticipantProfile(fullName, trackId),
    ),

  /** SREM on the Redis token set — logs out this device only. */
  logout: (email: string, token: string) =>
    call<{ msg: string }>(
      () => post('/logout', { email }, token),
      () => ({ msg: 'Logged out successfully' }),
    ),

  /* ---------------------------------------------------------- participant */

  getSlots: (email: string, token: string) =>
    call<GetSlotResponse>(
      () => post('/getSlot', { email, id_event: ID_EVENT }, token),
      () => mock.getSlots(),
    ),

  /* The UiTM attendance form address, handed out only during the event. Live only. */
  attendanceLink: (token: string) =>
    post<{ url: string }>('/attendance/link', { id_event: ID_EVENT }, token),

  getAccommodation: (email: string, token: string) =>
    call<AccommodationResponse>(
      () => post('/accommodation', { email, id_event: ID_EVENT }, token),
      () => mock.getAccommodation(),
    ),

  /**
   * `query` may be empty — that returns the whole roster for the event, which is how
   * the organiser answers "who still has no room?". `limit` is capped at 200 server-side.
   */
  listAccommodationParticipants: (
    token: string,
    query: string,
    filter: AccommodationFilter = 'all',
    limit = 50,
    offset = 0,
  ) =>
    call<AccommodationParticipantsResponse>(
      () => post(
        '/admin/accommodation/participants',
        { id_event: ID_EVENT, q: query, filter, limit, offset },
        token,
      ),
      () => mock.listAccommodationParticipants(query, filter, limit, offset),
    ),

  /**
   * `status: 'own_arrangement'` records that the delegate does not need a room from us
   * and must be sent with an empty `roomNumber` — the server rejects both at once
   * rather than guessing which was meant.
   */
  assignAccommodationRoom: (
    token: string,
    email: string,
    roomNumber: string,
    status: AccommodationStatus = 'assigned',
  ) =>
    call<AccommodationResponse>(
      () => post(
        '/admin/accommodation/assign',
        { id_event: ID_EVENT, participant_email: email, room_number: roomNumber, status },
        token,
      ),
      () => mock.assignAccommodationRoom(email, roomNumber, status),
    ),

  /* Organiser ticks the key handover at the hotel desk; false clears a mistaken tick. */
  setAccommodationKey: (token: string, email: string, collected: boolean) =>
    call<{ key_collected_at: string | null }>(
      () => post('/admin/accommodation/key', { id_event: ID_EVENT, participant_email: email, collected }, token),
      () => mock.setAccommodationKey(email, collected),
    ),

  importAccommodationRooms: (token: string, assignments: AccommodationImportItem[]) =>
    call<AccommodationImportResponse>(
      () => post('/admin/accommodation/import', { id_event: ID_EVENT, assignments }, token),
      () => mock.importAccommodationRooms(assignments),
    ),

  /**
   * `qr_uuid` comes off the scanned code; `slot_id_toscan` is the slot the user
   * tapped before opening the scanner. The server matches on both plus id_event,
   * so scanning the right code against the wrong slot is rejected.
   */
  checkIn: (email: string, token: string, qr_uuid: string, slot_id_toscan: number) =>
    call<CheckInResponse>(
      () => post('/checkIN', { id_event: ID_EVENT, email, qr_uuid, slot_id_toscan }, token),
      () => mock.checkIn(qr_uuid, slot_id_toscan),
    ),

  /*
   * The acoustic route into the same ledger. `sound_code` is what the phone decoded
   * from the room speakers; the server recomputes the HMAC for the slot's current and
   * previous 30-second window, and refuses outright while the event's toggle is off.
   */
  checkInBySound: (email: string, token: string, sound_code: string, slot_id_toscan: number) =>
    call<CheckInResponse>(
      () => post('/checkIN', { id_event: ID_EVENT, email, sound_code, slot_id_toscan }, token),
      () => mock.checkInBySound(sound_code, slot_id_toscan),
    ),

  /*
   * Staff, venue-scoped: the code the display should be playing right now. Also
   * takes the broadcast lease for `device`; a 409 with body.reason === 'busy' means
   * another device holds it, and `takeover` claims it anyway.
   */
  soundCode: (
    email: string,
    token: string,
    id_slot: number,
    device: { id: string; label: string },
    takeover = false,
  ) =>
    call<SoundCodeResponse>(
      () => post(
        '/sound/code',
        { id_event: ID_EVENT, email, id_slot, device: device.id, device_label: device.label, takeover },
        token,
      ),
      () => mock.soundCode(id_slot),
    ),

  /* The rotating QR payload to draw right now. Venue-scoped, no lease. */
  qrCode: (email: string, token: string, id_slot: number) =>
    call<QrCodeResponse>(
      () => post('/qr/code', { id_event: ID_EVENT, email, id_slot }, token),
      () => mock.qrCode(id_slot),
    ),

  /* Organiser: pin a slot to its static qr_uuid, or back to rotating. */
  setStaticQr: (email: string, token: string, id_slot: number, static_qr: boolean) =>
    call<{ ok: boolean; id_slot: number; static_qr: boolean }>(
      () => post('/admin/slots/static-qr', { id_event: ID_EVENT, email, id_slot, static_qr }, token),
      () => mock.setStaticQr(id_slot, static_qr),
    ),

  /* Venue-scoped attended/expected for the session on screen. */
  venueAttendance: (email: string, token: string, id_slot: number) =>
    call<VenueAttendanceResponse>(
      () => post('/venue/attendance', { id_event: ID_EVENT, email, id_slot }, token),
      () => mock.venueAttendance(id_slot),
    ),

  /*
   * Organiser-only. Records a check-in for someone else, subject to the same track and
   * qr_required rule as a scan. 404 `not_participant`, 409 `not_on_track`.
   */
  manualCheckIn: (email: string, token: string, id_slot: number, participant_email: string) =>
    call<ManualCheckInResponse>(
      () => post('/admin/checkin/manual', { id_event: ID_EVENT, email, id_slot, participant_email }, token),
      () => mock.manualCheckIn(id_slot, participant_email),
    ),

  /* Give the lease back on Stop, so the other device need not wait for it to lapse. */
  soundRelease: (email: string, token: string, id_slot: number, device: string) =>
    call<void>(
      () => post('/sound/release', { id_event: ID_EVENT, email, id_slot, device }, token),
      () => undefined,
    ),

  certStatus: (email: string) =>
    call<CertStatus>(
      () => post('/checkCertStatus', { email, id_event: ID_EVENT }),
      () => mock.certStatus(),
    ),

  /* --------------------------------------------------------------- venue */

  venueSlots: (email: string, token: string) =>
    call<QrGetSlotResponse>(
      () => post('/qr/getSlot', { email, id_event: ID_EVENT }, token),
      () => mock.venueSlots(),
    ),

  /* ---------------------------------------------------- events and venues */

  /*
   * Organiser tier. Managing these records does NOT change which event the app serves -
   * that is `event.is_current`. ID_EVENT is sent so the server can echo it back and the
   * screen can mark which record the app is actually serving.
   */
  adminEvents: (email: string, token: string) =>
    call<AdminEventsResponse>(
      () => post('/admin/events', { email, id_event: ID_EVENT }, token),
      () => { throw new ApiError(0, 'Event administration needs the live server.') },
    ),

  adminSaveEvent: (
    email: string, token: string,
    payload: {
      id: number | null; event_name: string; short_name: string; organizer: string
      venue: string; event_start: string; event_end: string; status: number
    },
  ) =>
    call<{ ok: boolean; id: number | null; created: boolean }>(
      () => post('/admin/events/save', { email, ...payload }, token),
      () => { throw new ApiError(0, 'Event administration needs the live server.') },
    ),

  /*
   * Changes which event the whole app serves, for everyone, on their next load. The
   * server refuses an archived event and swaps the flag in one transaction, because
   * UX_event_single_current is a real index rather than a convention.
   */
  adminSetCurrentEvent: (email: string, token: string, id: number) =>
    call<{ ok: boolean; id: number }>(
      () => post('/admin/events/current', { email, id }, token),
      () => { throw new ApiError(0, 'Event administration needs the live server.') },
    ),

  adminSaveVenue: (
    email: string, token: string,
    payload: { id: number | null; id_event: number | null; venue_name: string },
  ) =>
    call<{ ok: boolean; id: number | null; created: boolean }>(
      () => post('/admin/venues/save', { email, ...payload }, token),
      () => { throw new ApiError(0, 'Event administration needs the live server.') },
    ),

  /* ----------------------------------------------------- user administration */

  /*
   * Super admin only, all five. These hand somebody else the keys, so there is no mock
   * arm that pretends to succeed - demo mode has no user table to administer, and a
   * fixture that returned `ok: true` would teach the screen a lie.
   */
  adminUsers: (email: string, token: string) =>
    call<AdminUsersResponse>(
      () => post('/admin/users', { email, id_event: ID_EVENT }, token),
      () => { throw new ApiError(0, 'User administration needs the live server.') },
    ),

  adminSetRole: (email: string, token: string, id_user: number, id_role: number) =>
    call<{ ok: boolean }>(
      () => post('/admin/users/role', { email, id_user, id_role }, token),
      () => { throw new ApiError(0, 'User administration needs the live server.') },
    ),

  adminSetVenues: (email: string, token: string, id_user: number, venue_ids: number[]) =>
    call<{ ok: boolean }>(
      () => post('/admin/users/venues', { email, id_user, venue_ids }, token),
      () => { throw new ApiError(0, 'User administration needs the live server.') },
    ),

  /* The reply carries the plaintext once. Show it, do not persist it. */
  adminResetPassword: (email: string, token: string, id_user: number) =>
    call<AdminPasswordResponse>(
      () => post('/admin/users/password', { email, id_user }, token),
      () => { throw new ApiError(0, 'User administration needs the live server.') },
    ),

  adminCreateUser: (
    email: string, token: string,
    payload: { user_name: string; full_name: string; id_role: number; venue_ids: number[] },
  ) =>
    call<AdminCreateUserResponse>(
      () => post('/admin/users/create', { email, ...payload }, token),
      () => { throw new ApiError(0, 'User administration needs the live server.') },
    ),

  /* ------------------------------------------------- attendance reporting */

  /*
   * Event-wide, not venue-scoped, unlike the other staff routes - "who is short of the
   * threshold" cannot be answered a room at a time. Staff-gated on the server.
   */
  attendanceSummary: (email: string, token: string) =>
    call<AttendanceSummaryResponse>(
      () => post('/admin/attendance/summary', { email, id_event: ID_EVENT }, token),
      () => mock.attendanceSummary(),
    ),

  /*
   * `id_slot` absent means every session. One endpoint for both so the single-session
   * export and the whole-event export cannot produce different columns.
   */
  attendanceDetail: (email: string, token: string, id_slot?: number) =>
    call<AttendanceDetailResponse>(
      () => post('/admin/attendance/detail', { email, id_event: ID_EVENT, id_slot }, token),
      () => mock.attendanceDetail(id_slot),
    ),

  /*
   * Extending a session that has run over. Staff only, and the server additionally
   * checks the slot is in one of the caller's `qr_venue` rooms.
   *
   * `minutes` is the ABSOLUTE total, not a delta - two staff tapping "+15" would
   * otherwise produce 30 while each believed they had added 15. The reply carries the
   * value the row now holds, so the caller renders storage rather than intent.
   */
  extendSlot: (email: string, token: string, id_slot: number, minutes: number) =>
    call<ExtendSlotResponse>(
      () => post('/slot/extend', { email, id_event: ID_EVENT, id_slot, extended_time: minutes }, token),
      () => mock.extendSlot(id_slot, minutes),
    ),

  /*
   * The route answers with an array of rows, not an object - an event with no
   * setting_apps row yields [], which is why every field is defaulted here rather than
   * trusted. Readable by any signed-in participant: the Gallery and Resources screens
   * need the links, and only writing is staff-gated.
   */
  getSettings: (email: string, token: string) =>
    call<EventSettings>(
      async () => {
        const rows = await post<Array<Partial<EventSettings>>>(
          '/setting_apps/get', { email, id_event: ID_EVENT }, token,
        )
        const row = rows?.[0]
        return {
          show_cert: Boolean(row?.show_cert),
          gallery_url: row?.gallery_url || null,
          resources_url: row?.resources_url || null,
          programme_url: row?.programme_url || null,
          /* `??` and not `||`: 0 is a real threshold meaning every delegate qualifies. */
          certificate_threshold: row?.certificate_threshold ?? null,
          sound_checkin: Boolean(row?.sound_checkin),
          names_locked: Boolean(row?.names_locked),
          attendance_link: Boolean(row?.attendance_link),
          attendance_url: row?.attendance_url ?? null,
          attendance_on: Boolean(row?.attendance_on),
        }
      },
      () => mock.getSettings(),
    ),

  /*
   * `patch` is optional and partial on purpose: the server leaves out whatever is not
   * sent, so the certificate toggle can save itself without knowing about links or the
   * threshold, and vice versa. An empty string clears a link; null and undefined are
   * different things here, so do not collapse them.
   *
   * `certificate_threshold` is a whole percent, 0-100. The server divides by 100 on the
   * way into the column and multiplies on the way out — the fraction never travels.
   */
  updateSettings: (
    email: string,
    token: string,
    show_cert: boolean,
    patch?: {
      gallery_url?: string
      resources_url?: string
      programme_url?: string
      certificate_threshold?: number
      sound_checkin?: boolean
      names_locked?: boolean
      attendance_url?: string
      attendance_on?: boolean
    },
  ) =>
    call<void>(
      () => post(
        '/setting_apps/update',
        { email, show_cert, id_event: ID_EVENT, ...patch },
        token,
      ),
      () => mock.saveSettings(show_cert, patch),
    ),
  /* ----------------------------------------------------------- delegate pass */

  /*
   * Mint a pass for the signed-in account.
   *
   * The email is sent for the house's sake, not the server's: `/pass/issue` takes the
   * identity from the token and ignores the body for it. There is deliberately no mock
   * arm - a fixture pass would be a credential this client invented, and the whole
   * point of the feature is that it did not.
   */
  issuePass: (email: string, token: string) =>
    post<PassIssued>('/pass/issue', { email, id_event: ID_EVENT }, token),

  /*
   * Check a pass. Staff only, and live only.
   *
   * No mock arm for the same reason, and more sharply: a verifier that answers "valid"
   * from fixtures would wave people through on bad conference wifi. `PassScan` refuses
   * to render at all in mock mode rather than rely on this throwing.
   */
  verifyPass: (email: string, token: string, pass: string) =>
    post<PassVerdict>('/pass/verify', { email, pass, id_event: ID_EVENT }, token),

  /* Check a pass and record attendance for a session in the steward's venue. Same
     rules as verifyPass: staff only, live only, no mock arm. */
  checkinPass: (email: string, token: string, pass: string, id_slot: number) =>
    post<PassCheckinVerdict>('/pass/checkin', { email, pass, id_slot, id_event: ID_EVENT }, token),

  /*
   * Certificate setup. Organiser only, live only - the PDFs are files on the server.
   * Two of these move bytes rather than JSON (the template back, and a preview), and
   * the upload is multipart, so they go through `raw` below instead of `post`.
   */
  certificateState: (email: string, token: string) =>
    post<CertificateState>('/admin/certificate/state', { email, id_event: ID_EVENT }, token),

  certificateTemplate: (email: string, token: string) =>
    rawPdf('/admin/certificate/template/get', { email, id_event: ID_EVENT }, token),

  certificateUpload: async (email: string, token: string, file: File) => {
    const form = new FormData()
    form.append('id_event', String(ID_EVENT))
    form.append('email', email)
    form.append('file', file, file.name)
    const res = await fetch(`${API_BASE}/admin/certificate/template`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    })
    const text = await res.text()
    const body = text ? (JSON.parse(text) as Record<string, unknown>) : null
    if (!res.ok) throw new ApiError(res.status, String(body?.msg ?? res.statusText), body)
    return body as { ok: boolean; page: { width: number; height: number; pages: number } }
  },

  certificateSaveLayout: (email: string, token: string, layout: CertificateLayout) =>
    post<{ ok: boolean; layout: CertificateLayout }>('/admin/certificate/layout', { email, id_event: ID_EVENT, ...layout }, token),

  certificatePreview: (email: string, token: string, layout: CertificateLayout, name: string) =>
    rawPdf('/admin/certificate/preview', { email, id_event: ID_EVENT, layout, name }, token),

  /* scope 'all' ignores the attendance threshold: everyone not yet issued gets one. */
  certificateGenerate: (email: string, token: string, scope: 'eligible' | 'all' = 'eligible') =>
    post<CertificateGenerateResponse>('/admin/certificate/generate', { email, id_event: ID_EVENT, scope }, token),

  certificatePeople: (email: string, token: string) =>
    post<{ ok: boolean; people: CertificatePerson[] }>('/admin/certificate/people', { email, id_event: ID_EVENT }, token),

  /* One certificate on demand, eligible or not. Refused if already issued. */
  certificateGenerateOne: (email: string, token: string, targetEmail: string) =>
    post<{ ok: boolean; cert_no: string; cert_url: string; full_name: string | null; eligible: boolean }>(
      '/admin/certificate/generate-one', { email, id_event: ID_EVENT, target_email: targetEmail }, token),

  certificateIssued: (email: string, token: string) =>
    post<{ ok: boolean; issued: IssuedCertificate[] }>('/admin/certificate/issued', { email, id_event: ID_EVENT }, token),

  /* Same number, new PDF: for a corrected name or a changed template. */
  certificateRegenerate: (email: string, token: string, targetEmail: string) =>
    post<{ ok: boolean; cert_no: string; cert_url: string; full_name: string | null }>(
      '/admin/certificate/regenerate', { email, id_event: ID_EVENT, target_email: targetEmail }, token),

  /* The idea board. Fixtures keep a small in-memory board so the demo has a screen. */
  ideas: (email: string, token: string) =>
    call<{ ok: boolean; ideas: Idea[] }>(
      () => post('/ideas/list', { email, id_event: ID_EVENT }, token),
      () => mock.ideas(),
    ),
  postIdea: (email: string, token: string, body: string, category: IdeaCategory, module: IdeaModule) =>
    call<{ ok: boolean; id: number; status: IdeaStatus }>(
      () => post('/ideas/post', { email, id_event: ID_EVENT, body, category, module }, token),
      () => mock.postIdea(body, category, module),
    ),
  voteIdea: (email: string, token: string, id_idea: number) =>
    call<{ ok: boolean; id: number; voted: boolean; votes: number }>(
      () => post('/ideas/vote', { email, id_event: ID_EVENT, id_idea }, token),
      () => mock.voteIdea(id_idea),
    ),
  adminIdeas: (email: string, token: string) =>
    call<{ ok: boolean; ideas: Idea[] }>(
      () => post('/admin/ideas/list', { email, id_event: ID_EVENT }, token),
      () => mock.ideas(),
    ),
  reviewIdea: (email: string, token: string, id_idea: number, patch: { status?: IdeaStatus; pinned?: boolean }) =>
    call<{ ok: boolean }>(
      () => post('/admin/ideas/review', { email, id_event: ID_EVENT, id_idea, ...patch }, token),
      () => mock.reviewIdea(id_idea, patch),
    ),

  /* Announcements: the organiser's line to Home. */
  announcements: (email: string, token: string) =>
    call<{ ok: boolean; announcements: Announcement[] }>(
      () => post('/announcements/list', { email, id_event: ID_EVENT }, token),
      () => mock.announcements(),
    ),
  adminAnnouncements: (email: string, token: string) =>
    call<{ ok: boolean; announcements: Announcement[] }>(
      () => post('/admin/announcements/list', { email, id_event: ID_EVENT }, token),
      () => mock.announcements(),
    ),
  postAnnouncement: (email: string, token: string, body: string, level: AnnouncementLevel, expires_in_minutes: number) =>
    call<{ ok: boolean; id: number }>(
      () => post('/admin/announcements/post', { email, id_event: ID_EVENT, body, level, expires_in_minutes }, token),
      () => mock.postAnnouncement(body, level, expires_in_minutes),
    ),
  setAnnouncement: (email: string, token: string, id: number, active: boolean) =>
    call<{ ok: boolean }>(
      () => post('/admin/announcements/set', { email, id_event: ID_EVENT, id, active }, token),
      () => mock.setAnnouncement(id, active),
    ),

}

/* A POST whose reply is a PDF, not JSON. Errors still arrive as JSON and become ApiError. */
async function rawPdf(path: string, body: unknown, token: string): Promise<ArrayBuffer> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const text = await res.text()
    let parsed: Record<string, unknown> | null = null
    try { parsed = text ? (JSON.parse(text) as Record<string, unknown>) : null } catch { parsed = null }
    throw new ApiError(res.status, String(parsed?.msg ?? res.statusText), parsed)
  }
  return res.arrayBuffer()
}
